import { useEffect, useRef } from "react";
import { useAuthStore } from "@/store/useAuthStore";
import type {
  WriteMode,
  AiMode,
  CollabMode,
  ParticipantStatus,
  SpaceStatus,
} from "../store/useSpaceStore";
import type { TldrawCameraVector } from "../spacesPresenceUtils";

export type SpaceStateSnapshot = {
  aiMode: AiMode;
  collabMode: CollabMode;
  spaceStatus: SpaceStatus;
  writeMode: WriteMode;
};

type SpaceCtrlCallbacks = {
  onWriteModeChange: (writeMode: WriteMode) => void;
  onSpaceClosed: () => void;
  onSpacePaused?: () => void;
  onSpaceResumed?: () => void;
  onParticipantJoined?: (userId: string, joinedAt?: number) => void;
  onParticipantLeft?: (userId: string) => void;
  onParticipantStatus?: (userId: string, status: ParticipantStatus) => void;
  onAiModeChanged?: (aiMode: AiMode) => void;
  onCollabModeChanged?: (collabMode: CollabMode) => void;
  onSnapshotSaved?: (savedAt: Date) => void;
  onStudentCamera?: (userId: string, camera: TldrawCameraVector) => void;
  // Fired on every ctrl (re)connect with the current authoritative state.
  // Use to recover from missed events during a transient ctrl disconnect.
  onStateSnapshot?: (snapshot: SpaceStateSnapshot) => void;
  // Fired on every ctrl WS open / close so callers can drive a "Reconnecting…" UI.
  onConnectionChange?: (isConnected: boolean) => void;
};

type CtrlMsg =
  | { type: "write_mode_changed"; writeMode: WriteMode }
  | { type: "collab_mode_changed"; collabMode: CollabMode }
  | { type: "space_closed" }
  | { type: "space_paused" }
  | { type: "space_resumed" }
  | { type: "participant_joined"; userId: string; joinedAt?: number }
  | { type: "participant_left"; userId: string }
  | { type: "participant_status"; userId: string; status: ParticipantStatus }
  | { type: "ai_mode_changed"; aiMode: AiMode }
  | { type: "snapshot_saved"; savedAt: number }
  | { type: "student_camera"; userId: string; camera: TldrawCameraVector }
  | {
      type: "state_snapshot";
      aiMode: AiMode;
      collabMode: CollabMode;
      spaceStatus: SpaceStatus;
      writeMode: WriteMode;
    };

export type SpaceCtrlSender = (msg: {
  type: "camera_update";
  camera: TldrawCameraVector;
}) => void;

/**
 * Connects to the gateway ctrl WebSocket channel (/spaces/ctrl/:spaceId).
 * The channel is server-to-client only — the hook only receives push messages.
 *
 * Reconnects automatically with exponential backoff (1s → 2s → 4s … capped at 30s)
 * except when the server explicitly closes with code 4001 (auth failure).
 */
export function useSpaceCtrl(
  spaceId: string | undefined,
  token: string | null,
  callbacks: SpaceCtrlCallbacks,
): SpaceCtrlSender {
  // Store callbacks in a ref so the reconnect closure is never stale.
  const callbacksRef = useRef(callbacks);
  useEffect(() => {
    callbacksRef.current = callbacks;
  });

  const wsRef = useRef<WebSocket | null>(null);
  const send = useRef<SpaceCtrlSender>((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  });

  const spacesBase = import.meta.env.VITE_SPACES_BASE_URL as string;
  // Read once at hook init — safe because setActingTeacher always triggers a full
  // page reload, so this hook is always recreated with the current value.
  const actingTeacherId = useAuthStore.getState().actingTeacherId;
  const wsUrl =
    spaceId && token
      ? `${spacesBase.replace(/^http/, "ws")}/spaces/ctrl/${spaceId}?token=${token}${actingTeacherId ? `&actAsTeacherId=${actingTeacherId}` : ""}`
      : null;

  useEffect(() => {
    if (!wsUrl) return;

    let active = true;
    let retryCount = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let ws: WebSocket | null = null;

    function connect() {
      if (!active) return;

      ws = new WebSocket(wsUrl!);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!active) {
          ws?.close();
          return;
        }
        retryCount = 0;
        callbacksRef.current.onConnectionChange?.(true);
      };

      ws.onmessage = (event) => {
        let msg: unknown;
        try {
          msg = JSON.parse(event.data as string);
        } catch (err) {
          console.warn("[useSpaceCtrl] malformed ctrl frame:", err);
          return;
        }
        if (
          typeof msg !== "object" ||
          msg === null ||
          typeof (msg as Record<string, unknown>).type !== "string"
        ) {
          return;
        }

        const typed = msg as CtrlMsg;
        if (typed.type === "write_mode_changed") {
          callbacksRef.current.onWriteModeChange(typed.writeMode);
        } else if (typed.type === "collab_mode_changed") {
          callbacksRef.current.onCollabModeChanged?.(typed.collabMode);
        } else if (typed.type === "space_closed") {
          callbacksRef.current.onSpaceClosed();
        } else if (typed.type === "space_paused") {
          callbacksRef.current.onSpacePaused?.();
        } else if (typed.type === "space_resumed") {
          callbacksRef.current.onSpaceResumed?.();
        } else if (typed.type === "participant_joined") {
          callbacksRef.current.onParticipantJoined?.(
            typed.userId,
            typed.joinedAt,
          );
        } else if (typed.type === "participant_left") {
          callbacksRef.current.onParticipantLeft?.(typed.userId);
        } else if (typed.type === "participant_status") {
          callbacksRef.current.onParticipantStatus?.(
            typed.userId,
            typed.status,
          );
        } else if (typed.type === "ai_mode_changed") {
          callbacksRef.current.onAiModeChanged?.(typed.aiMode);
        } else if (typed.type === "snapshot_saved") {
          callbacksRef.current.onSnapshotSaved?.(new Date(typed.savedAt));
        } else if (typed.type === "student_camera") {
          callbacksRef.current.onStudentCamera?.(typed.userId, typed.camera);
        } else if (typed.type === "state_snapshot") {
          callbacksRef.current.onStateSnapshot?.({
            aiMode: typed.aiMode,
            collabMode: typed.collabMode,
            spaceStatus: typed.spaceStatus,
            writeMode: typed.writeMode,
          });
        }
      };

      ws.onclose = (event) => {
        ws = null;
        wsRef.current = null;
        if (!active) return;
        callbacksRef.current.onConnectionChange?.(false);
        // Code 4001 = explicit auth rejection — do not retry
        if (event.code === 4001) return;
        const delay = Math.min(1_000 * Math.pow(2, retryCount), 30_000);
        retryCount++;
        retryTimer = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        // onclose always fires after onerror — no extra action needed
      };
    }

    connect();

    return () => {
      active = false;
      if (retryTimer !== null) clearTimeout(retryTimer);
      ws?.close();
      wsRef.current = null;
    };
  }, [wsUrl]);

  return send.current;
}
